import { Component, OnInit, OnDestroy, HostListener, ElementRef } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { map, flatMap } from 'rxjs/operators';

import {
  WorkflowContextService,
  ClientService,
  SecurityService,
  DataService,
  SystemService,
  Utilities
} from 'src/app/services';

import {
  Actions,
  Client,
  Role,
} from 'src/app/models';

import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';

export interface TimeZoneInfo {
  Id: string;
  DisplayName: string;
}

@Component({
  selector: 'wm-settings-general',
  standalone: false,
  templateUrl: './settings-general.component.html',
  styleUrls: ['./settings-general.component.scss']
})
export class SettingsGeneralComponent implements OnInit, OnDestroy {
  client: Client;
  adminRoles: Role[];
  states: string[];
  timeZones: TimeZoneInfo[];

  form: UntypedFormGroup;
  clientName: AbstractControl;
  adminRole: AbstractControl;

  loadingClient = false;
  saving = false;
  canEditFeatureConfiguration = false;
  isNewClient = false;

  openDropdown: string | null = null;

  confirmData: CommonConfirmationModalData = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    yesNoBtn: true,
    inTime: 300,
    outTime: 200,
    status: 'default'
  };
  private pendingAction: 'logo' | 'header' | null = null;

  private clientSubscribe: Subscription;

  // Start Code added by Sanchit on 04/27/2026
  isToggleFlow = false;
  pendingToggleValue: boolean | null = null;
  isModeFlow = false;
  pendingMode: number | null = null;
  // End Code added by Sanchit on 04/27/2026

  constructor(
    public context: WorkflowContextService,
    private _clientSvc: ClientService,
    private _securitySvc: SecurityService,
    private _dataSvc: DataService,
    private _router: Router,
    private route: ActivatedRoute,
    private _systemSvc: SystemService,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private http: HttpClient,
    private eRef: ElementRef
  ) {
    this.form = this.fb.group({
      clientName: ['', Validators.required],
      adminRole: ['', Validators.required],
      clientState: ['', Validators.required],
      timeZone: ['', Validators.required],
      visibleToPublic: [false],
      useNewLayout: [false],
      mode: [0]
    });

    this.clientName = this.form.controls['clientName'];
    this.adminRole = this.form.controls['adminRole'];
  }

  @HostListener('document:click', ['$event'])
  clickout(event: any) {
    if (!this.eRef.nativeElement.contains(event.target)) {
        this.openDropdown = null;
    }
  }

  // Start Code added by Sanchit on 04/27/2026
  onToggleIntent() {
    const currentValue = this.form.get('useNewLayout')?.value;
    const nextValue = !currentValue;

    this.pendingToggleValue = nextValue;
    this.isToggleFlow = true;

    const isEnable = nextValue;

    this.confirmData = {
      ...this.confirmData,
      open: true,
      yesNoBtn: true,
      replaceYesWith: 'CONFIRM',
      replaceNoWith: 'CANCEL',
      modalDescp: isEnable
        ? 'You’re switching to the new layout'
        : 'You’re switching to the old layout',
      modalSubDescp: isEnable
        ? 'Please confirm you want to make this change to the site'
        : 'Please confirm you want to make this change to the site'
    };
  }
  // End Code added by Sanchit on 04/27/2026

  ngOnInit() {
    this._securitySvc.isLoginEntitled(Actions.DO_ANYTHING).subscribe(entitled => {
      this.canEditFeatureConfiguration = entitled;
      if (!entitled) {
        this.form.controls['useNewLayout'].disable();
        this.form.controls['mode'].disable({ emitEvent: false });
      } else {
        this.form.controls['mode'].enable({ emitEvent: false });
      }
    });

    this._dataSvc.getTimeZones().subscribe((tz: TimeZoneInfo[]) => this.timeZones = tz);
    this._dataSvc.getStates().subscribe(s => this.states = s);

    if (this.context.client) {
      this.loadClientDetails();
    } else {
      this.clientSubscribe = this.context.client$.subscribe(() => {
        this.loadClientDetails();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.clientSubscribe) {
      this.clientSubscribe.unsubscribe();
    }
  }

  loadClientDetails() {
    this.loadingClient = true;

    this._clientSvc.getClient(this.context.client.id)
      .pipe(
        map(client => {
          this.client = client;
          return this.client;
        }),
        flatMap(client => {
          return this._securitySvc.getRolesWithAllActions(
            client.id,
            Actions.CLIENT_ADMIN_ACTIONS
          );
        }),
        map(roles => {
          this.adminRoles = roles;
          return this.client;
        })
      )
      .subscribe(() => {
        this.form.patchValue({
          clientName: this.client.name,
          clientState: this.client.state,
          timeZone: this.client.timeZoneId,
          visibleToPublic: this.client.visibleToPublic,
          useNewLayout: this.client.useNewLayout,
          mode: this.client.mode
        });

        if (this.client.adminRole && this.adminRoles && this.adminRoles.length > 0) {
          this.adminRole.setValue(this.client.adminRole.id);
        }

        this.loadingClient = false;
      });
  }

  toggleDropdown(type: string, event: Event) {
    event.stopPropagation();
    if (this.openDropdown === type) {
        this.openDropdown = null;
    } else {
        this.openDropdown = type;
    }
  }

  selectOption(controlName: string, value: any) {
    if (controlName === 'mode' && this.canEditFeatureConfiguration && value !== this.form.get('mode').value) {
      this.pendingMode = value;
      this.isModeFlow = true;
      this.openDropdown = null;
      this.confirmData = {
        ...this.confirmData,
        open: true,
        yesNoBtn: true,
        replaceYesWith: 'CONFIRM',
        replaceNoWith: 'CANCEL',
        modalDescp: value === 1 ? 'Switch to Live Mode' : 'Switch to Test Mode',
        modalSubDescp: value === 1
          ? 'Live mode enables normal outbound email delivery. Existing data created in Test mode remains available in Live mode unless it has been archived.'
          : 'Test mode suppresses outbound email delivery. Existing and newly created data remains available throughout the system while this jurisdiction is in Test mode.'
      };
      return;
    }

    this.form.get(controlName).setValue(value);

    if (controlName === 'adminRole') {
        const selectedRole = this.adminRoles.find(r => r.id === value);
        if (selectedRole) {
            this.client.adminRole = selectedRole;
        }
    }

    this.openDropdown = null;
  }

  getRoleName(id: string): string {
    if (!id || !this.adminRoles) return '';
    const role = this.adminRoles.find(r => r.id === id);
    return role ? role.name : '';
  }

  getTimezoneName(id: string): string {
    if (!id || !this.timeZones) return '';
    const tz = this.timeZones.find(t => t.Id === id);
    return tz ? tz.DisplayName : '';
  }

  getModeName(mode: number): string {
    return mode === 1 ? 'Live' : 'Test';
  }

  onFileSelected(event: Event, type: 'logo' | 'header') {
    const element = event.target as HTMLInputElement;
    const file = element.files?.[0];
    if (file) {
      this.uploadDocument(file, type);
      element.value = '';
    }
  }

  onUploadButtonClick() {
    this.toastr.warning('Please select an image first by clicking the dropzone or dragging a file.');
  }

  private uploadDocument(file: File, type: 'logo' | 'header') {
    if (!file) {
      this.toastr.warning('Please select a valid image first.');
      return;
    }

    if (file.size > 20000000) {
        this.toastr.error('The file you uploaded is too large. Please upload a file below 20mb.');
        return;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('filename', file.name);
    formData.append('origName', file.name);

    const pathSegments = [this.client.id];
    if (type === 'header') {
        pathSegments.push('header');
    }

    pathSegments.forEach(segment => {
        formData.append('parentPath[]', segment);
    });

    const uploadUrl = `${Utilities.getRootURL()}/api/Document/upload`;

    this.http.post<any>(uploadUrl, formData, {
        reportProgress: true,
        observe: 'events'
    }).subscribe(
        (event) => {
            if (event.type === HttpEventType.Response) {
                const body = event.body;
                const newPath = body ? body.path : null;

                if (newPath) {
                    if (type === 'logo') {
                        this.client.logoURL = newPath;
                    } else {
                        this.client.headerLogoURL = newPath;
                    }
                    this.toastr.success('Image uploaded successfully');
                } else {
                    this.toastr.warning('Upload successful but no path returned');
                }
            }
        },
        (error) => {
            console.error('Upload Failed:', error);
            this.toastr.error(`Upload failed: ${error.status} ${error.statusText}`);
        }
    );
  }

  requestDeleteLogo(type: 'logo' | 'header') {
    const path = type === 'logo' ? this.client.logoURL : this.client.headerLogoURL;
    if (!path) return;

    this.pendingAction = type;
    const title = type === 'logo' ? 'Large Logo' : 'Header Logo';

    this.confirmData = {
      ...this.confirmData,
      open: true,
      modalDescp: `Delete ${title}`,
      modalSubDescp: `Are you sure you want to delete the ${title}? Deleting this logo will remove it from the application.`,
      yesNoBtn: true,
      replaceYesWith: 'DELETE',
      replaceNoWith: 'CANCEL'
    };
  }

  onConfirmModalYes() {
    this.confirmData = { ...this.confirmData, open: false };

    if (this.isModeFlow) {
      if (this.pendingMode !== null) {
        this.form.patchValue({ mode: this.pendingMode });
      }

      this.pendingMode = null;
      this.isModeFlow = false;
      return;
    }

    // Start Code added by Sanchit on 04/27/2026
    if (this.isToggleFlow) {
      if (this.pendingToggleValue !== null) {
        this.form.patchValue({
          useNewLayout: this.pendingToggleValue
        });
      }

      this.pendingToggleValue = null;
      this.isToggleFlow = false;
      return;
    }
    // End Code added by Sanchit on 04/27/2026

    if (this.pendingAction) {
        const type = this.pendingAction;
        const path = type === 'logo' ? this.client.logoURL : this.client.headerLogoURL;

        if (path) {
            this._dataSvc.deleteDocumentByPath(path).subscribe(() => {
                if (type === 'logo') {
                    this.client.logoURL = null as any;
                } else {
                    this.client.headerLogoURL = null as any;
                }
                this.toastr.info('Logo deleted');
            });
        }
    }

    this.pendingAction = null;
  }

  onConfirmModalNo() {
    this.confirmData = { ...this.confirmData, open: false };

    if (this.isModeFlow) {
      this.pendingMode = null;
      this.isModeFlow = false;
      return;
    }

    // Start Code added by Sanchit on 04/27/2026
    if (this.isToggleFlow) {
      this.pendingToggleValue = null;
      this.isToggleFlow = false;
      return;
    }
    // End Code added by Sanchit on 04/27/2026

    this.pendingAction = null;
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const originalMode = this.client.mode;
    const val = this.form.value;
    this.client.name = val.clientName;
    this.client.state = val.clientState;
    this.client.timeZoneId = val.timeZone;
    this.client.visibleToPublic = val.visibleToPublic;
    this.client.useNewLayout = val.useNewLayout;

    // Disabled controls are dropped from form.value, so only apply a mode the user may edit.
    if (this.canEditFeatureConfiguration) {
      this.client.mode = val.mode;
    }


    if (this.adminRoles && val.adminRole) {
        const fullRole = this.adminRoles.find(r => r.id === val.adminRole);
        if (fullRole) {
            this.client.adminRole = fullRole;
        }
    }

    this._clientSvc.saveClient(this.client).subscribe(
      res => {
        if (this.context.client) {
          this.context.client = res;
          this.context.client$.emit(res);
          this.context.useNewLayout$.emit(res.useNewLayout);
        } else {
          this.context.client = res;
        }

        this.saving = false;
        this.toastr.success('Settings Saved!');

        this.loadClientDetails();

        if (!res.useNewLayout) {
          this._router.navigate([
            '/admin/jurisdiction',
            res.id,
            'workflows'
          ]);
        } else {
          this._router.navigate(['./'], { relativeTo: this.route });
        }
      },
      err => {
        this.saving = false;
        if (this.canEditFeatureConfiguration) {
          this.client.mode = originalMode;
          this.form.get('mode').setValue(originalMode);
        }
        this.toastr.error('Error saving settings');
      }
    );
  }

  cancel() {
    this._router.navigate(['../../'], { relativeTo: this.route });
  }
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent, type: 'logo' | 'header') {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.uploadDocument(file, type)
    }
  }
}
